import { useEffect, useRef } from 'react';

function AnimatedLogo({ height = 40 }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const countRef = useRef(1000);
  const flagRef = useRef(1);

  const SPEED = 4;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const dpr = window.devicePixelRatio || 1;

    const cssHeight = height;
    const cssWidth = Math.floor(height * 5);

    canvas.style.height = `${cssHeight}px`;
    canvas.style.width = `${cssWidth}px`;

    canvas.height = cssHeight * dpr;
    canvas.width = cssWidth * dpr;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    function scale(num, in_min, in_max, out_min, out_max) {
      return (
        ((num - in_min) * (out_max - out_min)) /
          (in_max - in_min) +
        out_min
      );
    }

    function draw() {
      const count = countRef.current;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const logo_width = cssWidth;
      const logo_height = cssHeight;

      const letter_w = Math.floor(logo_width / 8);
      const lc_x = Math.floor(logo_width / 16);
      const lc_y = 0;
      const interSpace = Math.floor(logo_width / 16);

      countRef.current -= flagRef.current * SPEED;
      if (countRef.current < 0 || countRef.current > 1000) {
        flagRef.current *= -1;
      }

      ctx.beginPath();
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = '#fff';

      /** ---------- B ---------- **/
      ctx.moveTo(lc_x, lc_y);
      ctx.lineTo(lc_x + letter_w, lc_y);
      ctx.moveTo(lc_x, lc_y + logo_height);
      ctx.lineTo(lc_x + letter_w, lc_y + logo_height);
      ctx.moveTo(lc_x, lc_y);
      ctx.lineTo(lc_x, lc_y + logo_height);

      const a = scale(count, 10, 990, lc_x, lc_x + letter_w);
      const b = scale(count, 10, 990, logo_height / 20, 0);
      const c = scale(count, 10, 990, lc_x, lc_x + letter_w);

      ctx.moveTo(lc_x + letter_w, lc_y);
      ctx.bezierCurveTo(
        lc_x + letter_w,
        lc_y + logo_height * 0.5,
        a,
        lc_y + logo_height * 0.5 + b,
        c,
        lc_y + logo_height * 0.5
      );

      ctx.moveTo(lc_x + letter_w, lc_y + logo_height);
      ctx.bezierCurveTo(
        lc_x + letter_w,
        lc_y + logo_height * 0.5,
        a,
        lc_y + logo_height * 0.5 - b,
        c,
        lc_y + logo_height * 0.5
      );

      /** ---------- I ---------- **/
      ctx.moveTo(lc_x + letter_w + interSpace, lc_y);
      ctx.lineTo(lc_x + 2 * letter_w + interSpace, lc_y);
      ctx.moveTo(lc_x + letter_w + interSpace, lc_y + logo_height);
      ctx.lineTo(lc_x + 2 * letter_w + interSpace, lc_y + logo_height);

      const d = scale(count, 10, 990, lc_x + letter_w + interSpace + logo_height / 2, lc_x + letter_w + interSpace);
      const e = scale(count, 10, 990, lc_x + 2 * letter_w + interSpace - logo_height / 2, lc_x + 2 * letter_w + interSpace);

      ctx.moveTo(lc_x + letter_w + interSpace, lc_y);
      ctx.bezierCurveTo(d, lc_y + logo_height / 4, d, lc_y + logo_height - logo_height / 4, lc_x + letter_w + interSpace, lc_y + logo_height);
      ctx.moveTo(lc_x + 2 * letter_w + interSpace, lc_y);
      ctx.bezierCurveTo(e, lc_y + logo_height / 4, e, lc_y + logo_height - logo_height / 4, lc_x + 2 * letter_w + interSpace, lc_y + logo_height);

      /** ---------- N ---------- **/
      const f = scale(count, 10, 990, logo_height / 5, 0);
      const g = scale(count, 10, 990, logo_height / 7, 0);
      const h = scale(count, 10, 990, logo_height / 2, 0);

      ctx.moveTo(lc_x + 2 * letter_w + 2 * interSpace, lc_y);
      ctx.bezierCurveTo(lc_x + 3 * letter_w + 2 * interSpace - 25, lc_y + f, lc_x + 3 * letter_w + 2 * interSpace + g, lc_y + h, lc_x + 3 * letter_w + 2 * interSpace, lc_y);
      ctx.moveTo(lc_x + 2 * letter_w + 2 * interSpace, lc_y + logo_height);
      ctx.bezierCurveTo(lc_x + 2 * letter_w + 2 * interSpace - g, lc_y + logo_height - h, lc_x + 2 * letter_w + 2 * interSpace + 25, lc_y + logo_height - f, lc_x + 3 * letter_w + 2 * interSpace, lc_y + logo_height);

      ctx.moveTo(lc_x + 2 * letter_w + 2 * interSpace, lc_y);
      ctx.lineTo(lc_x + 2 * letter_w + 2 * interSpace, lc_y + logo_height);
      ctx.moveTo(lc_x + 3 * letter_w + 2 * interSpace, lc_y);
      ctx.lineTo(lc_x + 3 * letter_w + 2 * interSpace, lc_y + logo_height);

      /** ---------- Y ---------- **/
      const k = scale(count, 10, 990, logo_height / 2.2, 0);
      ctx.moveTo(lc_x + 3 * letter_w + 3 * interSpace, lc_y);
      ctx.bezierCurveTo(lc_x + 3 * letter_w + 3 * interSpace, lc_y + k, lc_x + 4 * letter_w + 3 * interSpace, lc_y + k, lc_x + 4 * letter_w + 3 * interSpace, lc_y);
      ctx.moveTo(lc_x + 3 * letter_w + 3 * interSpace + k, lc_y + logo_height);
      ctx.lineTo(lc_x + 4 * letter_w + 3 * interSpace - k, lc_y + logo_height);
      ctx.moveTo(lc_x + 3 * letter_w + 3 * interSpace, lc_y);
      ctx.lineTo(lc_x + 3 * letter_w + 3 * interSpace + k, lc_y + logo_height);
      ctx.moveTo(lc_x + 4 * letter_w + 3 * interSpace, lc_y);
      ctx.lineTo(lc_x + 4 * letter_w + 3 * interSpace - k, lc_y + logo_height);

      /** ---------- U ---------- **/
      const iU = scale(count, 10, 990, logo_height / 5.5, 0);
      const jU = scale(count, 10, 990, logo_height / 13, 0);
      const kU = scale(count, 10, 990, logo_height / 10, 0);

      ctx.moveTo(lc_x + 4 * letter_w + 4 * interSpace, lc_y);
      ctx.bezierCurveTo(lc_x + 4 * letter_w + 4 * interSpace + kU, lc_y + iU, lc_x + 5 * letter_w + 4 * interSpace - kU, lc_y + iU, lc_x + 5 * letter_w + 4 * interSpace, lc_y);
      ctx.moveTo(lc_x + 4 * letter_w + 4 * interSpace, lc_y + logo_height - jU);
      ctx.bezierCurveTo(lc_x + 4 * letter_w + 4 * interSpace + kU, lc_y + logo_height, lc_x + 5 * letter_w + 4 * interSpace - kU, lc_y + logo_height, lc_x + 5 * letter_w + 4 * interSpace, lc_y + logo_height - jU);
      ctx.moveTo(lc_x + 4 * letter_w + 4 * interSpace, lc_y);
      ctx.lineTo(lc_x + 4 * letter_w + 4 * interSpace, lc_y + logo_height - jU);
      ctx.moveTo(lc_x + 5 * letter_w + 4 * interSpace, lc_y);
      ctx.lineTo(lc_x + 5 * letter_w + 4 * interSpace, lc_y + logo_height - jU);


      ctx.stroke();
      animationRef.current = requestAnimationFrame(draw);
    }

    animationRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animationRef.current);
  }, [height]);

  return <canvas ref={canvasRef} style={{ display: 'block' }} />;
}

export default AnimatedLogo;
